import { useTheme } from '../context/ThemeContext';
import { useState, useEffect, useRef } from 'react';
import { getBalances, getBudgetSummary, getTransactions, syncPlaid, getIncome, getHousehold, getSavingsAdvisor, getMonthlyAdvisor } from '../api/client';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';


const fmt  = (n) => `$${Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtK = (n) => Math.abs(n) >= 1000 ? `$${(Math.abs(n)/1000).toFixed(1)}k` : fmt(n);
const pct  = (a, b) => b === 0 ? 0 : Math.min(100, Math.round((a / b) * 100));

const ProgressBar = ({ value, max, color = null, height = 6 }) => {
  const { COLORS } = useTheme();
  const resolvedColor = color || COLORS.accent;
  const p = pct(value, max);
  const barColor = p > 90 ? COLORS.red : p > 70 ? COLORS.amber : resolvedColor;
  return (
    <div style={{ height, background: COLORS.border, borderRadius: 99, overflow: "hidden" }}>
      <div style={{ width: `${p}%`, height: "100%", background: barColor, borderRadius: 99, transition: "width 0.6s ease" }} />
    </div>
  );
};

const Badge = ({ label, color }) => (
  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", padding: "3px 7px", borderRadius: 4, background: color + "22", color, border: `1px solid ${color}33` }}>
    {label}
  </span>
);

const StatCard = ({ label, value, sub, color, glow }) => {
  const { COLORS } = useTheme();
  const resolvedColor = color || COLORS.text;
  return (
  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: "16px 18px", flex: 1, boxShadow: glow ? `0 0 20px ${glow}` : "none" }}>
    <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>{label}</div>
    <div style={{ color: resolvedColor, fontSize: 20, fontWeight: 700, fontFamily: "'DM Mono', monospace", letterSpacing: -0.5 }}>{value}</div>
    {sub && <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 4 }}>{sub}</div>}
  </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  const { COLORS } = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "10px 14px" }}>
      <p style={{ color: COLORS.textMuted, fontSize: 11, margin: "0 0 6px" }}>Day {label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontSize: 12, fontWeight: 700, margin: "2px 0", fontFamily: "'DM Mono', monospace" }}>
          {p.name}: ${p.value?.toFixed(0)}
        </p>
      ))}
    </div>
  );
};

const SpendingChart = ({ title, subtitle, data, budgetKey, actualKey, budgetColor, actualColor, warningThreshold }) => {
  const { COLORS } = useTheme();
  const warningDay = data.findIndex(d => d[actualKey] != null && d[budgetKey] != null && d[actualKey] > d[budgetKey] * (warningThreshold / 100));
  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ marginBottom: 14 }}>
        <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>{title}</h3>
        <p style={{ color: COLORS.textMuted, fontSize: 12, margin: "4px 0 0" }}>{subtitle}</p>
      </div>
      <ResponsiveContainer width="100%" height={160}>
        <AreaChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id={`gb-${budgetKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={budgetColor} stopOpacity={0.15} />
              <stop offset="95%" stopColor={budgetColor} stopOpacity={0} />
            </linearGradient>
            <linearGradient id={`ga-${actualKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={actualColor} stopOpacity={0.2} />
              <stop offset="95%" stopColor={actualColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis dataKey="day" tick={{ fill: COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} interval={6} />
          <YAxis tick={{ fill: COLORS.textMuted, fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={v => `$${(v/1000).toFixed(1)}k`} />
          <Tooltip content={<CustomTooltip />} />
          {warningDay > 0 && <ReferenceLine x={data[warningDay]?.day} stroke={COLORS.amber} strokeDasharray="3 3" strokeOpacity={0.5} />}
          <Area type="monotone" dataKey={budgetKey} name="Planned" stroke={budgetColor} strokeWidth={1.5} strokeDasharray="5 3" fill={`url(#gb-${budgetKey})`} dot={false} connectNulls />
          <Area type="monotone" dataKey={actualKey} name="Actual" stroke={actualColor} strokeWidth={2} fill={`url(#ga-${actualKey})`} dot={false} connectNulls />
        </AreaChart>
      </ResponsiveContainer>
      <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 20, height: 2, background: budgetColor, borderRadius: 1, opacity: 0.6 }} />
          <span style={{ color: COLORS.textMuted, fontSize: 11 }}>Planned</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 20, height: 2, background: actualColor, borderRadius: 1 }} />
          <span style={{ color: COLORS.textMuted, fontSize: 11 }}>Actual</span>
        </div>
      </div>
    </div>
  );
};

const buildChartData = (transactions, categories, periodStart, periodLength) => {
  // FIX: use cat.amount (not cat.budgeted) — API returns amount from expense_categories table
  const totalBudget    = categories.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const varBudget      = categories.filter(c => c.type !== 'fixed').reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const dailyBudget    = totalBudget / periodLength;
  const dailyVarBudget = varBudget / periodLength;

  const today = new Date();

  const spendByDay = {}, varSpendByDay = {};
  transactions.filter(t => t.type === 'expense').forEach(t => {
    const txDate = new Date(t.date);
    txDate.setHours(0, 0, 0, 0);
    const d = Math.round((txDate - periodStart) / 86400000) + 1;
    if (d >= 1 && d <= periodLength) {
      const amt = parseFloat(t.amount);
      spendByDay[d] = (spendByDay[d] || 0) + amt;
      const cat = categories.find(c => c.id === t.category_id);
      if (cat && cat.type !== 'fixed') varSpendByDay[d] = (varSpendByDay[d] || 0) + amt;
    }
  });

  const todayOffset = Math.round((today - periodStart) / 86400000) + 1;
  const data = [];
  let cumActual = 0, cumVar = 0;
  for (let d = 1; d <= periodLength; d++) {
    cumActual += spendByDay[d] || 0;
    cumVar    += varSpendByDay[d] || 0;
    data.push({
      day: d,
      totalBudget: Math.round(dailyBudget * d),
      totalActual: d <= todayOffset ? Math.round(cumActual) : null,
      varBudget:   Math.round(dailyVarBudget * d),
      varActual:   d <= todayOffset ? Math.round(cumVar) : null,
    });
  }
  return data;
};

// ─── AI Analysis ─────────────────────────────────────────────────────────────
const AIAnalysis = ({ cats, transactions, totalCash, totalDebt }) => {
  const { COLORS } = useTheme();
  const [insights, setInsights] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [fetched, setFetched]   = useState(false);

  const run = async () => {
    if (fetched) return;
    setLoading(true);
    setFetched(true);
    try {
      // FIX: use cat.amount not cat.budgeted
      const totalBudget = cats.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
      const totalSpent  = cats.reduce((s, c) => s + parseFloat(c.spent || 0), 0);
      const topCats = cats.slice(0, 8).map(c =>
        `${c.name}: $${parseFloat(c.spent||0).toFixed(0)} of $${parseFloat(c.amount||0).toFixed(0)}`
      ).join(', ');

      const periodStartForAI = new Date();
      const msDay = periodStartForAI.getDate() >= (cats[0]?.household_month_start || 26) ? periodStartForAI : new Date(periodStartForAI.getFullYear(), periodStartForAI.getMonth() - 1, 26);
      const dayNum = Math.min(Math.round((new Date() - new Date(new Date().getFullYear(), new Date().getDate() >= 26 ? new Date().getMonth() : new Date().getMonth() - 1, 26)) / 86400000) + 1, 30);

      const prompt = `You are a household budget analyst. Give 3 short, specific, actionable insights (1 sentence each) based on this real data.
Budget period: ~30 days. Day ${dayNum} of the period.
Total budget: $${totalBudget.toFixed(0)}, spent so far: $${totalSpent.toFixed(0)} (${Math.round(totalSpent/totalBudget*100)}%).
Cash: $${totalCash.toFixed(0)}, debt: $${totalDebt.toFixed(0)}.
Categories (name: spent/budgeted): ${topCats}.
Return ONLY a JSON array of 3 strings, no preamble, no markdown. Example: ["insight 1","insight 2","insight 3"]`;

      const res = await fetch("/api/household/ai-analysis", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('ledger_token')}`
        },
        body: JSON.stringify({ prompt })
      });
      const data = await res.json();
      const text = data.content?.[0]?.text || "[]";
      const clean = text.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      setInsights(Array.isArray(parsed) ? parsed : []);
    } catch (e) {
      setInsights(["Unable to load analysis — check your connection."]);
    } finally {
      setLoading(false);
    }
  };

  const dotColor = (i) => [COLORS.green, COLORS.amber, COLORS.accent][i % 3];

  return (
    <div style={{ background: "linear-gradient(135deg, #0f172a, #1a1040)", border: `1px solid ${COLORS.accent}33`, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: insights.length > 0 ? 14 : 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18 }}>🤖</span>
          <h3 style={{ color: COLORS.accent, fontWeight: 700, fontSize: 15, margin: 0 }}>AI Budget Analysis</h3>
        </div>
        {!fetched && (
          <button onClick={run} style={{ padding: "6px 12px", background: COLORS.accentGlow, border: `1px solid ${COLORS.accent}44`, borderRadius: 8, color: COLORS.accent, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12 }}>
            Analyze
          </button>
        )}
        {fetched && !loading && (
          <button onClick={() => { setFetched(false); setInsights([]); setTimeout(run, 50); }} style={{ padding: "6px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, color: COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12 }}>
            ↻
          </button>
        )}
      </div>
      {loading && (
        <p style={{ color: COLORS.textMuted, fontSize: 13, margin: "10px 0 0", fontStyle: "italic" }}>Analyzing your budget...</p>
      )}
      {insights.map((insight, i) => (
        <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", borderTop: `1px solid ${COLORS.border}`, marginTop: i === 0 ? 4 : 0 }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: dotColor(i), flexShrink: 0, marginTop: 5 }} />
          <p style={{ color: COLORS.textMuted, fontSize: 13, margin: 0, lineHeight: 1.5 }}>{insight}</p>
        </div>
      ))}
    </div>
  );
};

// ─── Monthly Financial Advisor ────────────────────────────────────────────────
const MonthlyAdvisor = ({ COLORS }) => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await getMonthlyAdvisor();
        setData(res.data);
      } catch (e) { /* silent fail */ }
      finally { setLoading(false); }
    })();
  }, []);

  const focusStyle = {
    savings:  { color: COLORS.green, bg: COLORS.green + '18', border: COLORS.green + '44', icon: '🏦', label: 'Focus on Savings'  },
    debt:     { color: COLORS.red,   bg: COLORS.red   + '18', border: COLORS.red   + '44', icon: '💳', label: 'Pay Down Debt'     },
    balanced: { color: COLORS.amber, bg: COLORS.amber + '18', border: COLORS.amber + '44', icon: '⚖️', label: 'Balanced Approach' },
  };

  if (loading) return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
      <p style={{ color: COLORS.textMuted, fontSize: 13, margin: 0 }}>Building monthly strategy...</p>
    </div>
  );
  if (!data) return null;

  const fmt2  = (n) => `$${Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  const focus = data.recommendation?.focus || 'balanced';
  const fs    = focusStyle[focus] || focusStyle.balanced;

  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>🎯 Monthly Advisor</h3>
          <p style={{ color: COLORS.textMuted, fontSize: 12, margin: '3px 0 0' }}>{data.daysLeft} days left this period</p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>Savings</div>
            <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>{fmt2(data.totalSavings)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>Debt</div>
            <div style={{ color: COLORS.red, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>{fmt2(data.totalDebt)}</div>
          </div>
        </div>
      </div>

      {/* Strategy banner */}
      <div style={{ background: fs.bg, border: `1px solid ${fs.border}`, borderRadius: 10, padding: '12px 14px', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{ fontSize: 16 }}>{fs.icon}</span>
          <span style={{ color: fs.color, fontWeight: 700, fontSize: 14 }}>{fs.label}</span>
        </div>
        <p style={{ color: COLORS.textMuted, fontSize: 13, margin: 0, lineHeight: 1.6 }}>{data.recommendation?.message}</p>
      </div>

      {/* Action items */}
      {data.recommendation?.actions?.length > 0 && (
        <div>
          <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>This Month's Actions</div>
          {data.recommendation.actions.map((action, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 0', borderTop: i > 0 ? `1px solid ${COLORS.border}` : 'none' }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: fs.color + '22', border: `1px solid ${fs.color}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                <span style={{ color: fs.color, fontSize: 11, fontWeight: 700 }}>{i + 1}</span>
              </div>
              <p style={{ color: COLORS.textMuted, fontSize: 13, margin: 0, lineHeight: 1.5 }}>{action}</p>
            </div>
          ))}
        </div>
      )}

      {/* Savings goals progress */}
      {data.savingsAccounts?.filter(a => a.goal).length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${COLORS.border}` }}>
          <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>Savings Goals</div>
          {data.savingsAccounts.filter(a => a.goal).map((a, i) => (
            <div key={i} style={{ marginBottom: i < data.savingsAccounts.filter(x => x.goal).length - 1 ? 10 : 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: COLORS.text, fontSize: 12 }}>{a.name}</span>
                <span style={{ color: COLORS.textMuted, fontSize: 12 }}>{fmt2(a.balance)} / {fmt2(a.goal)} ({a.progress}%)</span>
              </div>
              <div style={{ height: 5, background: COLORS.border, borderRadius: 99, overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, a.progress)}%`, height: '100%', background: a.progress >= 100 ? COLORS.green : COLORS.accent, borderRadius: 99 }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Helpers (module scope to avoid minifier TDZ issues) ─────────────────────
const WEEKDAY_INDEX = { Sunday:0, Monday:1, Tuesday:2, Wednesday:3, Thursday:4, Friday:5, Saturday:6 };
const countWeekdaysInRange = (weekday, start, end) => {
  const target = WEEKDAY_INDEX[weekday];
  if (target === undefined) return 4;
  let count = 0;
  const d = new Date(start);
  while (d.getDay() !== target) d.setDate(d.getDate() + 1);
  while (d <= end) { count++; d.setDate(d.getDate() + 7); }
  return count;
};

// ─── Dashboard ────────────────────────────────────────────────────────────────
const Dashboard = ({ setScreen }) => {
  const { COLORS } = useTheme();
  const [balances,     setBalances]     = useState(null);
  const [summary,      setSummary]      = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [incomes,      setIncomes]      = useState([]);
  const [household,    setHousehold]    = useState(null);
  const [syncing,      setSyncing]      = useState(false);
  const [loading,      setLoading]      = useState(true);

  const budgetRef   = useRef(null);
  const categoryRef = useRef(null);
  const chartsRef   = useRef(null);
  const cardsRef    = useRef(null);
  const savingsRef  = useRef(null);
  const fixedBillsRef = useRef(null);
  const cashflowRef = useRef(null);
  const analysisRef = useRef(null);
  const savingsAdvisorRef = useRef(null);

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const NAV_PILLS = [
    { ref: budgetRef,          icon: "📊", label: "Budget"      },
    { ref: chartsRef,          icon: "📉", label: "Charts"      },
    { ref: fixedBillsRef,      icon: "🧾", label: "Fixed Bills" },
    { ref: categoryRef,        icon: "📋", label: "Categories"  },
    { ref: cardsRef,           icon: "💳", label: "Cards"       },
    { ref: savingsRef,         icon: "🏦", label: "Savings"     },
    { ref: cashflowRef,        icon: "📈", label: "Cash Flow"   },
    { ref: savingsAdvisorRef,  icon: "🎯", label: "Advisor"     },
    { ref: analysisRef,        icon: "🤖", label: "Analysis"    },
  ];

  const load = async () => {
    setLoading(true);
    // FIX: individual try/catches so one failing endpoint doesn't wipe out everything
    try {
      const hhRes = await getHousehold();
      const hhData = hhRes.data;
      setHousehold(Array.isArray(hhData) ? null : hhData?.data || hhData || null);
    } catch (err) { console.error('Household error:', err); }

    try {
      const balRes = await getBalances();
      setBalances(balRes.data);
    } catch (err) { console.error('Balances error:', err); }

    try {
      const sumRes = await getBudgetSummary();
      setSummary(sumRes.data);
    } catch (err) { console.error('Summary error:', err); }

    try {
      const incRes = await getIncome();
      const incData = incRes.data;
      setIncomes(Array.isArray(incData) ? incData : incData?.data || []);
    } catch (err) { console.error('Income error:', err); }

    try {
      const txRes = await getTransactions();
      const txData = txRes.data;
      setTransactions(Array.isArray(txData) ? txData : txData?.transactions || txData?.data || []);
    } catch (err) { console.error('Transactions error:', err); }

    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try { await syncPlaid(); await load(); }
    catch (err) { console.error(err); }
    finally { setSyncing(false); }
  };

  if (loading) return (
    <div style={{ padding: 24, color: COLORS.textMuted, textAlign: 'center', paddingTop: 80 }}>Loading...</div>
  );

  // FIX: use cat.amount not cat.budgeted throughout
  const cats         = summary?.categories || [];
  const totalBudget  = cats.reduce((a, c) => a + parseFloat(c.amount || 0), 0);

  const allAccounts  = balances?.accounts || [];
  const creditCards  = allAccounts.filter(a => a.type === 'credit');
  const checkingAccs   = allAccounts.filter(a => {
    if (a.type !== 'depository') return false;
    if (a.show_as_savings === true) return false;
    if (a.show_as_savings === false) return true; // explicitly moved to checking
    return a.subtype === 'checking';
  });
  const savingsAccs    = allAccounts.filter(a => {
    if (a.type !== 'depository') return false;
    if (a.show_as_savings === true) return true;
    if (a.show_as_savings === false) return false;
    return ['savings', 'money market', 'cd', 'hsa', 'cash management'].includes(a.subtype);
  });
  const investmentAccs = allAccounts.filter(a => a.type === 'investment');
  const totalCash    = parseFloat(balances?.summary?.totalCash || 0);
  const totalDebt    = parseFloat(balances?.summary?.totalDebt || 0);

  // Period config — read from household or fall back to defaults
  const monthStart  = household?.month_start || 26;
  const today = new Date();
  const periodStart = today.getDate() >= monthStart
    ? new Date(today.getFullYear(), today.getMonth(), monthStart)
    : new Date(today.getFullYear(), today.getMonth() - 1, monthStart);
  periodStart.setHours(0, 0, 0, 0);
  // Period end = (monthStart - 1) of the following month
  const periodEndDate = new Date(periodStart.getFullYear(), periodStart.getMonth() + 1, monthStart - 1);
  periodEndDate.setHours(23, 59, 59, 999);

  // Filter transactions to current billing period only
  const periodTxns = transactions.filter(t => {
    const [y, m, d] = (t.date || '').split('T')[0].split('-');
    if (!y) return false;
    const txDate = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
    return txDate >= periodStart && txDate <= periodEndDate;
  });

  // Compute per-category spending from periodTxns only (not all-time from summary)
  // Exclude cc_payment transactions from all budget/spending calculations
  const budgetTxns = periodTxns.filter(t => t.type === 'expense' && t.category_id !== 41);
  const spentByCat = budgetTxns.reduce((acc, t) => {
    if (t.category_id) acc[t.category_id] = (acc[t.category_id] || 0) + parseFloat(t.amount);
    return acc;
  }, {});
  const periodCats   = cats.map(c => ({ ...c, spent: spentByCat[c.id] || 0 }));
  const totalSpent   = cats.reduce((a, c) => a + parseFloat(c.spent || 0), 0);
  const remaining    = totalBudget - totalSpent;
  const moneyIn  = periodTxns.filter(t => t.type === 'income').reduce((s, t) => s + parseFloat(t.amount), 0);
  const moneyOut = periodTxns.filter(t => (t.type === 'expense' || t.type === 'transfer') && t.category_id !== 41).reduce((s, t) => s + parseFloat(t.amount), 0);
  const moneyOutReal = budgetTxns.reduce((s, t) => s + parseFloat(t.amount), 0);
  const periodLength  = Math.round((periodEndDate - periodStart) / 86400000) + 1;
  const dayOfPeriod   = Math.min(Math.round((today - periodStart) / 86400000) + 1, periodLength);
  const daysLeft     = periodLength - dayOfPeriod;
  const dailySpend   = moneyOutReal / Math.max(dayOfPeriod, 1);
  const chartData    = buildChartData(budgetTxns, periodCats, periodStart, periodLength);

  // Planned income: count actual pay periods within the 26th→25th budget window
  const periodEnd = new Date(periodStart);
  periodEnd.setDate(periodEnd.getDate() + periodLength - 1);
  const plannedIncome = incomes.reduce((sum, src) => {
    const amt = parseFloat(src.amount || 0);
    if (src.schedule_type === 'monthly')      return sum + amt;
    if (src.schedule_type === 'twiceMonthly') return sum + (amt * 2);
    if (src.schedule_type === 'weekly')       return sum + (amt * countWeekdaysInRange(src.week_day, periodStart, periodEnd));
    if (src.schedule_type === 'biweekly')     return sum + (amt * Math.ceil(countWeekdaysInRange(src.week_day, periodStart, periodEnd) / 2));
    return sum + amt;
  }, 0);
  const remainingIncome = Math.max(0, plannedIncome - moneyIn);
  const projectedEOM = Math.round(totalCash + remainingIncome - (dailySpend * daysLeft));

  const fixedCats      = periodCats.filter(c => c.type === 'fixed');
  const fixedTotal     = fixedCats.reduce((s, c) => s + parseFloat(c.amount || 0), 0);
  const fixedPaid      = fixedCats.reduce((s, c) => s + parseFloat(c.spent  || 0), 0);
  const fixedRemaining = Math.max(0, fixedTotal - fixedPaid);

  return (
    <div style={{ padding: "0 0 100px", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ padding: "20px 16px 10px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <p style={{ color: COLORS.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", margin: 0 }}>
              {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
            </p>
            <h2 style={{ color: COLORS.text, fontSize: 22, fontWeight: 800, margin: "4px 0 0", letterSpacing: -0.5 }}>Monthly Overview</h2>
          </div>
          <button onClick={handleSync} disabled={syncing} style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "5px 10px", color: COLORS.textMuted, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {syncing ? '⟳ Syncing...' : '⟳ Sync'}
          </button>
        </div>
      </div>

      {/* Nav pills */}
      <div style={{ padding: "10px 12px 14px", display: "flex", gap: 7, overflowX: "auto", scrollbarWidth: "none" }}>
        {NAV_PILLS.map((p, i) => (
          <button key={i} onClick={() => scrollTo(p.ref)} style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 5, padding: "7px 13px", borderRadius: 20, border: `1px solid ${COLORS.border}`, background: COLORS.surface, color: COLORS.textMuted, cursor: "pointer", fontFamily: "inherit", fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}>
            <span style={{ fontSize: 13 }}>{p.icon}</span>{p.label}
          </button>
        ))}
      </div>

      <div style={{ padding: "0 12px", display: "flex", flexDirection: "column", gap: 12 }}>

        {/* ── Monthly Progress ── */}
        <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <span style={{ color: COLORS.text, fontWeight: 700, fontSize: 14 }}>Monthly Progress</span>
            <span style={{ color: COLORS.textMuted, fontSize: 13 }}>Day {dayOfPeriod} of {periodLength}</span>
          </div>
          <ProgressBar value={totalSpent} max={totalBudget} />
        </div>

        {/* ── Budget stat cards ── */}
        <div ref={budgetRef} style={{ scrollMarginTop: 52, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", gap: 10 }}>
            <StatCard label="Planned Budget" value={fmt(totalBudget)} sub="Total budgeted this period" />
            <StatCard label="Actual Spending" value={fmt(totalSpent)} sub={`${pct(totalSpent, totalBudget)}% used`} color={COLORS.amber} glow={COLORS.amberGlow} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <StatCard label="Planned Income" value={fmt(plannedIncome)} sub="Expected this period" color={COLORS.green} glow={COLORS.greenGlow} />
            <StatCard label="Income Received" value={fmt(moneyIn)} sub={`${pct(moneyIn, plannedIncome)}% received`} color={moneyIn >= plannedIncome ? COLORS.green : COLORS.text} />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <StatCard label="Cash Available" value={fmt(totalCash)} sub="Across all accounts" color={COLORS.green} />
            <StatCard label="Net Worth" value={`${(totalCash - totalDebt) < 0 ? '-' : ''}${fmt(totalCash - totalDebt)}`} sub="Cash minus debt" color={(totalCash - totalDebt) >= 0 ? COLORS.green : COLORS.red} />
          </div>
          <div style={{ background: remaining > 0 ? COLORS.greenGlow : COLORS.redGlow, border: `1px solid ${remaining > 0 ? COLORS.green : COLORS.red}44`, borderRadius: 14, padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
            <div>
              <div style={{ color: remaining > 0 ? COLORS.green : COLORS.red, fontSize: 11, fontWeight: 700, letterSpacing: 1.1, textTransform: "uppercase", marginBottom: 4 }}>Budget Remaining</div>
              <div style={{ color: remaining > 0 ? COLORS.green : COLORS.red, fontSize: 26, fontWeight: 800, fontFamily: "'DM Mono', monospace", letterSpacing: -1 }}>
                {remaining < 0 ? "-" : ""}{fmt(Math.abs(remaining))}
              </div>
              <div style={{ color: COLORS.textMuted, fontSize: 12, marginTop: 4 }}>Planned budget minus actual spending</div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ color: COLORS.textMuted, fontSize: 11, marginBottom: 6 }}>{pct(totalSpent, totalBudget)}% used · Day {dayOfPeriod} of {periodLength}</div>
              <div style={{ width: 100, height: 8, background: COLORS.border, borderRadius: 99, overflow: "hidden" }}>
                <div style={{ width: `${pct(totalSpent, totalBudget)}%`, height: "100%", background: remaining > 0 ? COLORS.green : COLORS.red, borderRadius: 99 }} />
              </div>
            </div>
          </div>
        </div>

        {/* ── Charts ── */}
        <div ref={chartsRef} style={{ scrollMarginTop: 52, display: "flex", flexDirection: "column", gap: 12 }}>
          <SpendingChart
            title="Total Spending"
            subtitle="Fixed + variable combined vs your full monthly budget"
            budgetKey="totalBudget" actualKey="totalActual"
            budgetColor={COLORS.accent} actualColor={COLORS.amber}
            warningThreshold={80} data={chartData}
          />
          <SpendingChart
            title="Variable Spending Trend"
            subtitle="Groceries, eating out, gas, misc — where it gets away from you"
            budgetKey="varBudget" actualKey="varActual"
            budgetColor={COLORS.green} actualColor={COLORS.amber}
            warningThreshold={65} data={chartData}
          />
        </div>

        {/* ── Fixed Bills ── */}
        <div ref={fixedBillsRef} style={{ scrollMarginTop: 52, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>🧾 Fixed Bills</h3>
              <p style={{ color: COLORS.textMuted, fontSize: 12, margin: "3px 0 0" }}>Recurring expenses this period</p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Remaining</div>
              <div style={{ color: fixedRemaining > 0 ? COLORS.amber : COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 16 }}>
                {fmt(fixedRemaining)}
              </div>
            </div>
          </div>

          {/* Summary row — always visible */}
          <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
            <div style={{ flex: 1, background: COLORS.greenGlow, border: `1px solid ${COLORS.green}33`, borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ color: COLORS.green, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 4 }}>Paid</div>
              <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 16 }}>{fmt(fixedPaid)}</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>
                {fixedCats.filter(c => parseFloat(c.spent || 0) >= parseFloat(c.amount || 0) * 0.9).length} of {fixedCats.length} bills
              </div>
            </div>
            <div style={{ flex: 1, background: fixedRemaining > 0 ? COLORS.amberGlow || "rgba(245,158,11,0.1)" : COLORS.greenGlow, border: `1px solid ${fixedRemaining > 0 ? COLORS.amber : COLORS.green}33`, borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ color: fixedRemaining > 0 ? COLORS.amber : COLORS.green, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 4 }}>Still Due</div>
              <div style={{ color: fixedRemaining > 0 ? COLORS.amber : COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 16 }}>{fmt(fixedRemaining)}</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>
                {fixedCats.filter(c => parseFloat(c.spent || 0) < parseFloat(c.amount || 0) * 0.9).length} bills pending
              </div>
            </div>
          </div>

          {/* Per-bill rows — scrollable */}
          {fixedCats.length === 0 && (
            <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: "center", padding: "12px 0" }}>No fixed categories — add them in Settings</p>
          )}
          <div style={{ maxHeight: 400, overflowY: "auto", scrollbarWidth: "none" }}>
            {fixedCats.map((cat, i) => {
              const spent    = parseFloat(cat.spent  || 0);
              const budgeted = parseFloat(cat.amount || 0);
              const isPaid   = spent >= budgeted * 0.9;
              const isOver   = spent > budgeted * 1.05;
              const statusColor = isOver ? COLORS.red : isPaid ? COLORS.green : COLORS.amber;
              const statusIcon  = isOver ? "⚠️" : isPaid ? "✓" : "○";
              return (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: `1px solid ${COLORS.border}` }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: statusColor + "22", border: `1px solid ${statusColor}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: isPaid || isOver ? 12 : 10, color: statusColor, fontWeight: 700, flexShrink: 0 }}>
                    {statusIcon}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cat.name}</div>
                    {cat.due_day && <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 1 }}>Due day {cat.due_day}</div>}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ color: statusColor, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 13 }}>{fmt(spent)}</div>
                    <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 1 }}>of {fmt(budgeted)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Category breakdown ── */}
        <div ref={categoryRef} style={{ scrollMarginTop: 52, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>Category Breakdown</h3>
              <button onClick={() => setScreen('transactions')} style={{ background: 'none', border: 'none', color: COLORS.accent, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>See all →</button>
            </div>
            {cats.length === 0 && (
              <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: 'center', padding: '20px 0' }}>No categories yet — add them in Settings</p>
            )}
            <div style={{ maxHeight: 520, overflowY: "auto", scrollbarWidth: "none" }}>
            {periodCats.map((cat, i) => {
              const spent    = parseFloat(cat.spent || 0);
              const budgeted = parseFloat(cat.amount || 0);
              return (
                <div key={i} style={{ marginBottom: i < periodCats.length - 1 ? 14 : 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                      <span style={{ color: COLORS.text, fontSize: 13 }}>{cat.name}</span>
                      <Badge label={cat.type || 'fixed'} color={cat.type === "fixed" ? COLORS.accent : cat.type === "variable" ? COLORS.green : COLORS.amber} />
                    </div>
                    <span style={{ color: spent > budgeted ? COLORS.red : COLORS.text, fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                      {fmt(spent)}<span style={{ color: COLORS.textMuted }}> / {fmt(budgeted)}</span>
                    </span>
                  </div>
                  <ProgressBar value={spent} max={budgeted} />
                </div>
              );
            })}
            </div>
          </div>
        </div>

        {/* ── Credit Cards ── */}
        <div ref={cardsRef} style={{ scrollMarginTop: 52, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>💳 Credit Cards</h3>
              <p style={{ color: COLORS.textMuted, fontSize: 12, margin: "3px 0 0" }}>This period's activity & balance changes</p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Total Debt</div>
              <div style={{ color: COLORS.red, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 16 }}>{fmt(totalDebt)}</div>
            </div>
          </div>
          {creditCards.length === 0 && <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}>No credit cards connected</p>}
          {creditCards.map((card, i) => {
            const charged    = periodTxns.filter(t => t.account_id === card.id && t.type === 'expense' && t.category_id !== 41).reduce((s, t) => s + parseFloat(t.amount || 0), 0);
            const paid       = periodTxns.filter(t => t.account_id === card.id && t.category_id === 41).reduce((s, t) => s + parseFloat(t.amount || 0), 0);
            const netChange  = charged - paid;
            const startBal   = parseFloat(card.balance || 0) - netChange;
            const wentUp     = netChange > 0;
            const wentDown   = netChange < 0;
            return (
              <div key={i} style={{ paddingBottom: i < creditCards.length - 1 ? 16 : 0, marginBottom: i < creditCards.length - 1 ? 16 : 0, borderBottom: i < creditCards.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                {/* Card name + current balance */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div>
                    <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>{card.custom_name || card.name}</div>
                    <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2, textTransform: 'capitalize' }}>{card.subtype}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: COLORS.red, fontFamily: "'DM Mono', monospace", fontSize: 15, fontWeight: 700 }}>{fmt(parseFloat(card.balance))}</div>
                    <div style={{ fontSize: 10, color: COLORS.textMuted, marginTop: 2 }}>current balance</div>
                  </div>
                </div>
                {/* Activity row */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ flex: 1, background: COLORS.bg, borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 3 }}>Started</div>
                    <div style={{ color: COLORS.text, fontFamily: "'DM Mono', monospace", fontWeight: 600, fontSize: 13 }}>{fmt(Math.max(0, startBal))}</div>
                  </div>
                  <div style={{ flex: 1, background: COLORS.bg, borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 3 }}>Charged</div>
                    <div style={{ color: COLORS.red, fontFamily: "'DM Mono', monospace", fontWeight: 600, fontSize: 13 }}>+{fmt(charged)}</div>
                  </div>
                  <div style={{ flex: 1, background: COLORS.bg, borderRadius: 8, padding: '8px 10px' }}>
                    <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 3 }}>Paid</div>
                    <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 600, fontSize: 13 }}>{paid > 0 ? `-${fmt(paid)}` : fmt(0)}</div>
                  </div>
                </div>
                {/* Net change indicator */}
                <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8, background: wentUp ? COLORS.red + '12' : wentDown ? COLORS.green + '12' : COLORS.border + '44', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: COLORS.textMuted, fontSize: 11 }}>
                    {wentUp ? '⚠️ Balance increased this period' : wentDown ? '✅ Balance decreased this period' : '➖ No net change this period'}
                  </span>
                  <span style={{ color: wentUp ? COLORS.red : wentDown ? COLORS.green : COLORS.textMuted, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 12 }}>
                    {wentUp ? '+' : wentDown ? '-' : ''}{fmt(Math.abs(netChange))}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Checking ── */}
        {checkingAccs.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div>
                <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>🏧 Checking</h3>
                <p style={{ color: COLORS.textMuted, fontSize: 12, margin: "3px 0 0" }}>Cash & spending accounts</p>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Total Cash</div>
                <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 16 }}>
                  {fmt(checkingAccs.reduce((s, a) => s + parseFloat(a.balance || 0), 0))}
                </div>
              </div>
            </div>
            {checkingAccs.map((acc, i) => (
              <div key={`chk-${i}`} style={{ paddingBottom: i < checkingAccs.length - 1 ? 14 : 0, marginBottom: i < checkingAccs.length - 1 ? 14 : 0, borderBottom: i < checkingAccs.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>{acc.custom_name || acc.name}</div>
                    <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2, textTransform: 'capitalize' }}>Checking</div>
                  </div>
                  <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>{fmt(parseFloat(acc.balance))}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Savings ── */}
        <div ref={savingsRef} style={{ scrollMarginTop: 52, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div>
              <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>🏦 Savings</h3>
              <p style={{ color: COLORS.textMuted, fontSize: 12, margin: "3px 0 0" }}>Balances, contributions & goals</p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Total Saved</div>
              <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 16 }}>
                {fmt(savingsAccs.reduce((s, a) => s + parseFloat(a.balance || 0), 0))}
              </div>
            </div>
          </div>
          {savingsAccs.length === 0 && (
            <p style={{ color: COLORS.textMuted, fontSize: 13, textAlign: 'center' }}>No savings accounts</p>
          )}
          {savingsAccs.map((acc, i) => {
            const balance = parseFloat(acc.balance || 0);
            const goal = acc.is_savings_goal && acc.goal_amount ? parseFloat(acc.goal_amount) : null;
            const pct  = goal ? Math.min(100, Math.round((balance / goal) * 100)) : null;
            const remaining = goal ? Math.max(0, goal - balance) : null;
            const barColor = pct >= 100 ? COLORS.green : pct >= 60 ? COLORS.accent : COLORS.amber;
            return (
              <div key={`sav-${i}`} style={{ paddingBottom: i < savingsAccs.length - 1 ? 16 : 0, marginBottom: i < savingsAccs.length - 1 ? 16 : 0, borderBottom: i < savingsAccs.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: goal ? 8 : 0 }}>
                  <div>
                    <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>{acc.goal_name || acc.custom_name || acc.name}</div>
                    <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2, textTransform: 'capitalize' }}>{acc.subtype}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>{fmt(balance)}</div>
                    {goal && <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2 }}>of {fmt(goal)}</div>}
                  </div>
                </div>
                {goal && (
                  <div>
                    <div style={{ background: COLORS.border, borderRadius: 4, height: 6, overflow: 'hidden', marginBottom: 6 }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: barColor, borderRadius: 4, transition: 'width 0.4s ease' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: barColor, fontSize: 11, fontWeight: 700 }}>{pct}% complete</span>
                      <span style={{ color: COLORS.textMuted, fontSize: 11 }}>{fmt(remaining)} remaining</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── Investments ── */}
        {investmentAccs.length > 0 && (
          <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div>
                <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>📊 Investments</h3>
                <p style={{ color: COLORS.textMuted, fontSize: 12, margin: "3px 0 0" }}>Retirement & investment accounts</p>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Total Value</div>
                <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 16 }}>
                  {fmt(investmentAccs.reduce((s, a) => s + parseFloat(a.balance || 0), 0))}
                </div>
              </div>
            </div>
            {investmentAccs.map((acc, i) => (
              <div key={i} style={{ paddingBottom: i < investmentAccs.length - 1 ? 14 : 0, marginBottom: i < investmentAccs.length - 1 ? 14 : 0, borderBottom: i < investmentAccs.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ color: COLORS.text, fontSize: 13, fontWeight: 600 }}>{acc.custom_name || acc.name}</div>
                    <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 2, textTransform: 'capitalize' }}>{acc.subtype?.toUpperCase()}</div>
                  </div>
                  <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>{fmt(parseFloat(acc.balance || 0))}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Cash Flow ── */}
        <div ref={cashflowRef} style={{ scrollMarginTop: 52, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: "16px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 14 }}>
            <span style={{ fontSize: 15 }}>📈</span>
            <div>
              <h3 style={{ color: COLORS.text, fontWeight: 700, fontSize: 15, margin: 0 }}>Cash Flow</h3>
              <p style={{ color: COLORS.textMuted, fontSize: 12, margin: "2px 0 0" }}>Money in vs out · running balance · projection</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <div style={{ flex: 1, background: COLORS.greenGlow, border: `1px solid ${COLORS.green}33`, borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ color: COLORS.green, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Money In</div>
              <div style={{ color: COLORS.green, fontFamily: "'DM Mono', monospace", fontWeight: 800, fontSize: 22 }}>{fmt(moneyIn)}</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 4 }}>Paychecks + transfers</div>
            </div>
            <div style={{ flex: 1, background: COLORS.redGlow, border: `1px solid ${COLORS.red}33`, borderRadius: 12, padding: "14px 16px" }}>
              <div style={{ color: COLORS.red, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 }}>Money Out</div>
              <div style={{ color: COLORS.red, fontFamily: "'DM Mono', monospace", fontWeight: 800, fontSize: 22 }}>{fmt(moneyOutReal)}</div>
              <div style={{ color: COLORS.textMuted, fontSize: 11, marginTop: 4 }}>All expenses this period</div>
            </div>
          </div>
          {checkingAccs.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 10 }}>Account Balances</div>
              {checkingAccs.map((acc, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: i < checkingAccs.length - 1 ? `1px solid ${COLORS.border}` : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: COLORS.accent, flexShrink: 0 }} />
                    <span style={{ color: COLORS.text, fontSize: 13 }}>{acc.custom_name || acc.name}</span>
                  </div>
                  <div style={{ color: COLORS.text, fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 14 }}>{fmt(parseFloat(acc.balance))}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ background: "linear-gradient(135deg, #0f172a, #1e1b4b)", border: `1px solid ${COLORS.accent}33`, borderRadius: 10, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ color: COLORS.accent, fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 4 }}>Projected End-of-Month</div>
              <div style={{ color: COLORS.textMuted, fontSize: 12 }}>Based on current income &amp; spending pace</div>
            </div>
            <div style={{ color: projectedEOM >= 0 ? COLORS.green : COLORS.red, fontFamily: "'DM Mono', monospace", fontWeight: 800, fontSize: 22 }}>
              {projectedEOM < 0 ? "-" : ""}{fmtK(Math.abs(projectedEOM))}
            </div>
          </div>
        </div>

        {/* ── Monthly Advisor ── */}
        <div ref={savingsAdvisorRef} style={{ scrollMarginTop: 52 }}>
          <MonthlyAdvisor COLORS={COLORS} />
        </div>

        {/* ── AI Analysis ── */}
        <div ref={analysisRef} style={{ scrollMarginTop: 52 }}>
          <AIAnalysis cats={periodCats} transactions={periodTxns} totalCash={totalCash} totalDebt={totalDebt} />
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
